import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  ArrowLeft, Play, Pause, Loader2, User, Headphones,
  AlertTriangle, CheckCircle2, XCircle, MessageSquare, Volume2, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import AudioPlayer from '../components/AudioPlayer';

function ScoreGauge({ score, label, max = 10, reason }) {
  const pct = (score / max) * 100;
  const color = pct >= 75 ? '#34D399' : pct >= 50 ? '#FBBF24' : '#FB7185';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-xs font-medium tabular-nums">{score}/{max}</span>
        </div>
        <div className="h-1.5 bg-border rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
        {reason && <p className="text-xs text-muted-foreground mt-1 leading-tight">{reason}</p>}
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-2 shadow-lg">
      <p className="text-xs">{payload[0]?.value?.toFixed(1)}</p>
    </div>
  );
}

function isAgentVagueStatement(statement, speakerRoles) {
  const speakerValue = String(statement?.speaker || '').trim();
  const normalizedSpeaker = speakerValue.toLowerCase();

  if (normalizedSpeaker === 'agent') {
    return true;
  }

  if (normalizedSpeaker === 'patient' || normalizedSpeaker === 'caller') {
    return false;
  }

  const speakerId = normalizedSpeaker.replace(/^speaker\s+/, '');
  return String(speakerRoles?.[speakerValue] || speakerRoles?.[normalizedSpeaker] || speakerRoles?.[speakerId] || '').trim().toLowerCase() === 'agent';
}

// Older Gemini/Gemma analyses stored flagged statements as plain strings
// instead of {text, speaker, severity, reason} objects. Normalize both shapes
// so legacy calls still render. Legacy strings are treated as agent statements
// (the prompt only ever asked the model to flag agent statements).
function normalizeGeminiStatement(statement) {
  if (typeof statement === 'string') {
    const text = statement.trim();
    return text ? { text, speaker: 'agent', severity: '', reason: '' } : null;
  }
  if (statement && typeof statement === 'object') return statement;
  return null;
}

// Older analyses stored sentiment as a single descriptive string (e.g.
// "Positive" or "Neutral-to-Negative - ..."); newer ones use
// {agent, patient}. Normalize so both render.
function normalizeGeminiSentiment(sentiment) {
  if (!sentiment) return null;
  if (typeof sentiment === 'string') {
    const overall = sentiment.trim();
    return overall ? { overall } : null;
  }
  if (typeof sentiment === 'object') return sentiment;
  return null;
}

function formatDetectedLanguage(value) {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '-';

  const labels = {
    te: 'Telugu',
    ta: 'Tamil',
    hi: 'Hindi',
    ml: 'Malayalam',
    kn: 'Kannada',
    other: 'Other',
  };

  return labels[key] || 'Other';
}

function isAnsweredCall(call) {
  return Number(call?.answered_seconds ?? 0) >= 30;
}

// Translate a raw pipeline error into an accurate, human-readable message.
// "Transcript is empty" / "Soniox returned 0 segments" are not system errors —
// they mean the recording contained no recognizable speech (e.g. it is only a
// ringtone or silence), so there is nothing to transcribe or analyze. Say that
// plainly instead of surfacing the cryptic internal error.
function describeProcessingError(rawError) {
  const msg = String(rawError || '').trim();
  if (/transcript is empty|0 segments|no[_ ]?speech/i.test(msg)) {
    return 'No speech detected in recording (ringtone/silence) — nothing to transcribe or analyze.';
  }
  return msg || 'unknown error';
}

export default function CallDetailPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isSupervisor, isAgent, user } = useAuth();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingCall, setProcessingCall] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState(isAgent ? 'analysis' : 'transcript');
  const audioRef = useRef(null);

  const fetchCall = useCallback(async () => {
    try {
      const [callRes, tagRes] = await Promise.all([
        api.getCallDetail(callId),
        api.getTagsForCall(callId).catch(() => ({ tags: [], tag_count: 0 })),
      ]);

      setCall({
        ...callRes,
        tags: tagRes?.tags || [],
        tag_count: tagRes?.tag_count || 0,
      });
    } catch (err) {
      toast.error('Failed to load call details');
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => { fetchCall(); }, [fetchCall]);

  useEffect(() => {
    if (!call) return;
    // A call can now be analyzed by Claude AND Gemma at the same time, in which
    // case all four data tabs are visible. Compute the visible tab set the same
    // way the tab strip does and only redirect when the active tab is hidden.
    const hasClaude = call.analysis && Object.keys(call.analysis).length > 0 && call.analysis.overall_score !== undefined;
    const gAnalysis = call.gemini_analysis || {};
    const hasGAnalysis = gAnalysis && Object.keys(gAnalysis).length > 0;
    const gtRaw = call.gemini_transcript;
    const gt = typeof gtRaw === 'string'
      ? (() => { try { return JSON.parse(gtRaw) || {}; } catch { return {}; } })()
      : (gtRaw || {});
    const hasGTranscript = Array.isArray(gt?.segments) && gt.segments.length > 0;
    // Gemma tabs show when Gemma produced output, or as the fallback when
    // Claude never ran (so the "not yet processed" empty state still renders).
    const showGemini = hasGTranscript || hasGAnalysis || !hasClaude;

    const visible = ['transcript', 'analysis', 'gemini-translation', 'gemini-analysis', 'voice'].filter((tab) => {
      if (isAgent && tab === 'transcript') return false;
      if (tab === 'voice') return true;
      if (tab === 'transcript' || tab === 'analysis') return hasClaude;
      return showGemini;
    });

    if (!visible.includes(activeTab)) {
      setActiveTab(visible[0] || 'voice');
    }
  }, [call, activeTab, isAgent]);

  // Single "Process Call" action with a transcript-aware branch:
  //   • transcript EMPTY  → run the full pipeline: Soniox transcription →
  //                         translation → Claude analysis (api.processCall).
  //   • transcript EXISTS → run Claude analysis only (api.analyzeCall).
  const handleProcessCall = async () => {
    setProcessingCall(true);
    const hasTranscript = Array.isArray(call?.transcript?.segments) && call.transcript.segments.length > 0;
    try {
      if (hasTranscript) {
        const res = await api.analyzeCall(callId);
        toast.success(res?.message || 'Analysis started');
        setTimeout(fetchCall, 3000);
      } else {
        await api.processCall(callId);
        toast.success('Processing started — transcription, translation, AI analysis & voice analysis');
        setTimeout(fetchCall, 5000);
      }
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('already being processed')) {
        toast.info('This call is already being processed');
      } else {
        toast.error('Failed to start processing');
      }
    } finally {
      setProcessingCall(false);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!call) {
    return <div className="text-center py-12 text-muted-foreground">Call not found</div>;
  }

  // The "AI Analysis" tab shows ONLY Claude's analysis. Gemini output
  // belongs in the dedicated "Gemini Analysis" tab and must never bleed
  // into this tab, even if Claude has not run yet.
  const claudeAnalysis = call.analysis || null;
  const hasClaudeAnalysis = claudeAnalysis && Object.keys(claudeAnalysis).length > 0 && claudeAnalysis.overall_score !== undefined;
  const analysis = hasClaudeAnalysis ? claudeAnalysis : {};
  const analysisSourceModel = hasClaudeAnalysis ? (call.anthropic_model || null) : null;

  const transcript = call.transcript || {};
  const baseSegments = transcript.segments || [];
  // Voice Metrics tab prefers the Claude-pipeline output (call.voice_metrics).
  // If only the Gemini pipeline ran (Soniox transcript + Gemma analysis), fall
  // back to gemini_voice_metrics so the tab still has data. The Claude column
  // is never overwritten by the Gemini pipeline (see migration 024).
  const claudeVoiceMetrics = call.voice_metrics || null;
  const geminiVoiceMetricsRaw = call.gemini_voice_metrics || null;
  const geminiVoiceMetrics = typeof geminiVoiceMetricsRaw === 'string'
    ? (() => { try { return JSON.parse(geminiVoiceMetricsRaw) || null; } catch { return null; } })()
    : geminiVoiceMetricsRaw;
  const hasClaudeVoiceMetrics = Boolean(claudeVoiceMetrics && claudeVoiceMetrics.pitch);
  const voiceMetrics = hasClaudeVoiceMetrics ? claudeVoiceMetrics : (geminiVoiceMetrics || {});
  const voiceMetricsSource = hasClaudeVoiceMetrics
    ? 'Claude'
    : (geminiVoiceMetrics && geminiVoiceMetrics.pitch ? 'Gemini (Soniox + Gemma)' : null);
  const tags = call.tags || [];
  const speakerRoles = analysis.speaker_roles || {};
  const vagueStatements = (analysis.vague_statements || []).filter((statement) => isAgentVagueStatement(statement, speakerRoles));

  // Gemini cron pipeline output (separate from Claude). See migration 019.
  const geminiTranscriptRaw = call.gemini_transcript || {};
  const geminiTranscript = typeof geminiTranscriptRaw === 'string'
    ? (() => { try { return JSON.parse(geminiTranscriptRaw) || {}; } catch { return {}; } })()
    : geminiTranscriptRaw;
  const geminiSegments = Array.isArray(geminiTranscript?.segments) ? geminiTranscript.segments : [];

  // Build the Transcript-tab segments. The base `transcript` field is the
  // Soniox + Claude-translated output. If Claude translation never ran for a
  // segment (english_text === original_text — still Indic script), fall back
  // to the matching Gemini-translated english_text so the Transcript tab
  // never displays untranslated Indic text in the "English" slot.
  const segments = baseSegments.map((seg, i) => {
    const baseOriginal = String(seg.original_text ?? seg.text ?? '');
    const baseEnglish = String(seg.english_text ?? seg.text ?? '');
    const isBaseUntranslated = !baseEnglish || baseEnglish === baseOriginal;
    const gSeg = geminiSegments[i];
    const gEnglish = String(gSeg?.english_text ?? '');
    if (isBaseUntranslated && gSeg && gEnglish && gEnglish !== baseOriginal) {
      return {
        ...seg,
        original_text: baseOriginal,
        english_text: gEnglish,
        is_translated: Boolean(gSeg.is_translated) || gEnglish !== baseOriginal,
        source_lang: gSeg.source_lang || seg.source_lang || null,
      };
    }
    return seg;
  });
  const geminiAnalysis = call.gemini_analysis || {};
  const geminiSpeakerRoles = geminiAnalysis.speaker_roles || {};
  const geminiVagueStatements = (geminiAnalysis.vague_statements || [])
    .map(normalizeGeminiStatement)
    .filter(Boolean)
    .filter((statement) => isAgentVagueStatement(statement, geminiSpeakerRoles));
  const geminiUnprofessionalStatements = (geminiAnalysis.unprofessional_statements || [])
    .map(normalizeGeminiStatement)
    .filter(Boolean);
  const geminiSentiment = normalizeGeminiSentiment(geminiAnalysis.sentiment);
  const hasGeminiTranscript = geminiSegments.length > 0;
  const hasGeminiAnalysis = Boolean(geminiAnalysis && Object.keys(geminiAnalysis).length > 0);
  // Gemma tabs show when Gemma produced output, or as the fallback when Claude
  // never ran. When both pipelines ran, Claude and Gemma tabs are shown side by
  // side (Claude → Transcript + AI Analysis, Gemma → Gemini Translation +
  // Gemini Analysis).
  const showGeminiTabs = hasGeminiTranscript || hasGeminiAnalysis || !hasClaudeAnalysis;
  const geminiOverallScore = geminiAnalysis.overall_score;
  const geminiScoreColor = geminiOverallScore >= 75 ? 'text-emerald-400' : geminiOverallScore >= 60 ? 'text-amber-400' : 'text-rose-400';
  const inProgress = ['downloading', 'transcribing', 'translating', 'analyzing', 'voice_analyzing'].includes(call.processing_status);

  // Status shown in the meta strip is driven by the Gemma pipeline
  // (gemini_status) rather than the legacy Claude processing_status.
  // gemini_status values: NULL (not picked up yet), 'processing',
  // 'completed', 'failed', 'archived'. While 'processing', derive the
  // sub-stage from how far the pipeline has progressed so the user sees
  // transcribing → translating → analyzing instead of a flat "processing".
  const hasBaseTranscript = Boolean(String(call.transcript ?? '').trim());
  const hasGeminiTranscriptRaw = Boolean(
    typeof geminiTranscriptRaw === 'string'
      ? geminiTranscriptRaw.trim()
      : (geminiTranscriptRaw && Object.keys(geminiTranscriptRaw).length > 0)
  );
  const geminiStatus = call.gemini_status || null;
  const geminiDisplayStatus = (() => {
    switch (geminiStatus) {
      case 'completed':
        return 'completed';
      case 'failed':
        return 'failed';
      case 'archived':
        return 'archived';
      case 'processing':
        if (!hasBaseTranscript) return 'transcribing';
        if (!hasGeminiTranscriptRaw) return 'translating';
        if (!hasGeminiAnalysis) return 'analyzing';
        return 'processing';
      default:
        // Not yet picked up by the pipeline (gemini_status IS NULL). Surface
        // every such call as "new" — whether or not it is eligible — to stay
        // consistent with the "New" status used across the calls list and
        // agent analytics views.
        return 'new';
    }
  })();
  const geminiStatusColor = geminiDisplayStatus === 'completed'
    ? 'bg-emerald-500/15 text-emerald-300'
    : geminiDisplayStatus === 'failed'
      ? 'bg-rose-500/15 text-rose-300'
      : geminiDisplayStatus === 'archived'
        ? 'bg-slate-500/15 text-slate-300'
        : geminiDisplayStatus === 'new'
          ? 'bg-blue-500/15 text-blue-300'
          : 'bg-amber-500/15 text-amber-300';

  // The "Process Call" / "Analyze" buttons run the Claude pipeline ONLY and
  // never touch gemini_status. So once a call is being processed by Claude
  // (or already has Claude analysis), drive the meta-strip status from the
  // Claude processing_status instead of the Gemma status — otherwise a call
  // reprocessed via Claude would stay stuck on a stale Gemma 'failed' badge.
  const showClaudeStatus = hasClaudeAnalysis || inProgress;
  const claudeDisplayStatus = call.processing_status || 'new';
  const displayStatus = showClaudeStatus ? claudeDisplayStatus : geminiDisplayStatus;
  const displayStatusColor = showClaudeStatus
    ? (claudeDisplayStatus === 'completed'
      ? 'bg-emerald-500/15 text-emerald-300'
      : claudeDisplayStatus === 'failed'
        ? 'bg-rose-500/15 text-rose-300'
        : claudeDisplayStatus === 'new'
          ? 'bg-blue-500/15 text-blue-300'
          : 'bg-amber-500/15 text-amber-300')
    : geminiStatusColor;
  const hasTranscriptAndTranslation = segments.length > 0;

  const canProcessCall = (isAdmin || isSupervisor)
    && isAnsweredCall(call)
    && !hasTranscriptAndTranslation
    && (call.processing_status === 'new' || call.processing_status === 'failed')
    && call.recording_url;

  const canAnalyzeCall = (isAdmin || isSupervisor)
    && isAnsweredCall(call)
    && hasTranscriptAndTranslation
    && !call.analysis
    && !inProgress;

  const overallScore = analysis.overall_score;
  const scoreColor = overallScore >= 75 ? 'text-emerald-400' : overallScore >= 60 ? 'text-amber-400' : 'text-rose-400';

  // Voice chart data
  const pitchData = (voiceMetrics.pitch?.timeline || []).map((v, i) => ({ idx: i, value: v }));
  const energyData = (voiceMetrics.energy?.rms_timeline || []).map((v, i) => ({ idx: i, value: v }));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Call Detail</h1>
          <p className="text-xs text-muted-foreground font-mono">{call.call_id}</p>
        </div>
        <div className="flex items-center gap-2">
          {(canProcessCall || canAnalyzeCall) && (
            <Button size="sm" onClick={handleProcessCall} disabled={processingCall} data-testid="call-detail-process-btn">
              {processingCall ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
              Analyze Call
            </Button>
          )}
        </div>
      </div>

      {/* Meta strip */}
      <div className="bg-card border border-border rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-9 gap-4">
        <div><span className="text-xs text-muted-foreground block">Agent</span><span className="text-sm font-medium">{call.agent_name}</span></div>
        <div><span className="text-xs text-muted-foreground block">Caller</span><span className="text-sm font-mono">{call.client_number}</span></div>
        <div><span className="text-xs text-muted-foreground block">Date</span><span className="text-sm">{call.date} {call.time}</span></div>
        <div><span className="text-xs text-muted-foreground block">Duration</span><span className="text-sm tabular-nums">{call.call_duration}s</span></div>
        <div><span className="text-xs text-muted-foreground block">Direction</span><span className={`text-xs px-2 py-0.5 rounded ${call.direction === 'inbound' ? 'bg-sky-500/15 text-sky-300' : 'bg-teal-500/15 text-teal-300'}`}>{call.direction}</span></div>
        <div><span className="text-xs text-muted-foreground block">Status</span><span className={`text-xs px-2 py-0.5 rounded ${displayStatusColor}`}>{displayStatus}</span></div>
        <div><span className="text-xs text-muted-foreground block">Language</span><span className="text-sm font-medium">{formatDetectedLanguage(call.detected_language)}</span></div>
        {!isAgent && hasClaudeAnalysis && (
          <div><span className="text-xs text-muted-foreground block">Score(Claude)</span>{overallScore !== undefined ? <span className={`text-lg font-bold tabular-nums ${scoreColor}`}>{overallScore}</span> : <span className="text-sm text-muted-foreground">-</span>}</div>
        )}
        {!isAgent && (hasGeminiAnalysis || !hasClaudeAnalysis) && (
          <div><span className="text-xs text-muted-foreground block">Score(Gemma)</span>{geminiOverallScore !== undefined ? <span className={`text-lg font-bold tabular-nums ${geminiScoreColor}`}>{geminiOverallScore}</span> : <span className="text-sm text-muted-foreground">-</span>}</div>
        )}
      </div>

      {tags.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-teal-400" />
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Matched Tags</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <div key={tag.id || tag.name} className="px-3 py-1.5 rounded-lg bg-teal-500/10 border border-teal-500/20">
                <p className="text-xs font-medium text-teal-300">{tag.name}</p>
                {(tag.matched_phrases || []).length > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Matched: {tag.matched_phrases.join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audio Player */}
      {call.recording_url && (
        <AudioPlayer src={call.recording_url} fallbackDuration={call.answered_seconds} />
      )}


      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {['transcript', 'analysis', 'gemini-translation', 'gemini-analysis', 'voice']
          .filter(tab => !isAgent || tab !== 'transcript')
          .filter(tab => {
            if (tab === 'voice') return true;
            if (tab === 'transcript' || tab === 'analysis') return hasClaudeAnalysis;
            return showGeminiTabs;
          })
          .map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`} data-testid={`call-detail-tab-${tab}`}
            >
              {tab === 'transcript' ? 'Transcript'
                : tab === 'analysis' ? 'AI Analysis'
                  : tab === 'voice' ? 'Voice Metrics'
                    : tab === 'gemini-translation' ? 'Gemini Translation'
                      : 'Gemini Analysis'}
            </button>
          ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'transcript' && (
        <div className="space-y-2" data-testid="call-detail-transcript">
          {segments.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
              {call.processing_status === 'completed' ? 'No transcript available' : 'Call not yet processed.'}
            </div>
          ) : segments.map((seg, i) => {
            const role = speakerRoles['Speaker ' + seg.speaker] || speakerRoles[seg.speaker] || (seg.speaker === '1' ? 'agent' : 'patient');
            const isAgent = role === 'agent';
            const isPatient = (role === 'patient') || (role === 'caller');
            const label = isAgent ? 'Agent' : isPatient ? 'Patient/Caller' : `Speaker ${seg.speaker}`;
            const labelColor = isAgent ? 'text-teal-300' : isPatient ? 'text-sky-300' : 'text-muted-foreground';
            const segClass = isAgent ? 'speaker-agent' : isPatient ? 'speaker-caller' : 'bg-card border border-border';
            const originalText = String(seg.original_text ?? seg.text ?? '').trim();
            const englishText = String(seg.english_text ?? seg.text ?? '').trim();
            const showOriginal = originalText && englishText && originalText !== englishText;
            return (
              <div key={i} className={`rounded-lg p-3 ${segClass}`}>
                <div className="flex items-center gap-2 mb-1">
                  {isAgent ? <Headphones className="w-3.5 h-3.5 text-teal-400" /> : <User className="w-3.5 h-3.5 text-sky-400" />}
                  <span className={`text-xs font-medium ${labelColor}`}>
                    {label}
                  </span>
                  <span className="text-[10px] text-muted-foreground font-mono">Speaker {seg.speaker}</span>
                  {seg.start_ms !== undefined && (
                    <span className="text-[10px] text-muted-foreground font-mono">{(seg.start_ms / 1000).toFixed(1)}s</span>
                  )}
                  {seg.source_lang && seg.source_lang !== 'en' && seg.source_lang !== 'unknown' && (
                    <span className="text-[10px] text-muted-foreground font-mono uppercase">{seg.source_lang}</span>
                  )}
                </div>
                {showOriginal && (
                  <p className="text-xs text-muted-foreground mb-1 italic">Original: {originalText}</p>
                )}
                <p className="text-sm">{englishText}</p>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'analysis' && (
        <div className="space-y-4" data-testid="call-detail-analysis-tabs">
          {hasClaudeAnalysis && (
            <div className="text-[11px] text-muted-foreground font-mono">
              Source: Claude{analysisSourceModel && ` · ${analysisSourceModel}`}
            </div>
          )}
          {!hasClaudeAnalysis ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
              No Claude analysis available{hasGeminiAnalysis ? '. See the Gemini Analysis tab for Gemini output.' : ''}
            </div>
          ) : (
            <>
              {/* Score overview */}
              <div className="bg-card border border-border rounded-xl p-4">
                {!isAgent && (
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`text-4xl font-bold tabular-nums ${scoreColor}`}>{overallScore}</div>
                    <div>
                      <p className="text-sm font-medium">Overall Quality Score</p>
                      <p className="text-xs text-muted-foreground">{analysis.call_purpose?.replace(/_/g, ' ')} - {analysis.call_outcome?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <ScoreGauge score={analysis.grammar_score || 0} label="Grammar" reason={analysis.grammar_score_reason} />
                  <ScoreGauge score={analysis.professionalism_score || 0} label="Professionalism" reason={analysis.professionalism_score_reason} />
                  <ScoreGauge score={analysis.empathy_score || 0} label="Empathy" reason={analysis.empathy_score_reason} />
                  <ScoreGauge score={analysis.communication_clarity || 0} label="Clarity" reason={analysis.communication_clarity_reason} />
                  <ScoreGauge score={analysis.greeting_quality || 0} label="Greeting" reason={analysis.greeting_quality_reason} />
                  <ScoreGauge score={analysis.closing_quality || 0} label="Closing" reason={analysis.closing_quality_reason} />
                  <ScoreGauge score={analysis.solution_provided?.score || 0} label="Solution" reason={analysis.solution_provided?.reason} />
                </div>
              </div>

              {/* Flags */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Solution */}
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {analysis.solution_provided?.provided ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
                    <span className="text-sm font-medium">Solution {analysis.solution_provided?.provided ? 'Provided' : 'Not Provided'}</span>
                  </div>
                  {analysis.solution_provided?.evidence && <p className="text-xs text-muted-foreground italic">{analysis.solution_provided.evidence}</p>}
                </div>

                {/* Escalation */}
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {analysis.escalation_detected?.detected ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                    <span className="text-sm font-medium">Escalation {analysis.escalation_detected?.detected ? 'Detected' : 'Not Detected'}</span>
                  </div>
                  {analysis.escalation_detected?.reason && <p className="text-xs text-muted-foreground">{analysis.escalation_detected.reason}</p>}
                </div>

                {/* Blame Shifting */}
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {analysis.blame_shifting?.detected ? <AlertTriangle className="w-4 h-4 text-rose-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                    <span className="text-sm font-medium">Blame Shifting {analysis.blame_shifting?.detected ? 'Detected' : 'Not Detected'}</span>
                  </div>
                  {analysis.blame_shifting?.evidence && <p className="text-xs text-muted-foreground italic">{analysis.blame_shifting.evidence}</p>}
                </div>

                {/* Sentiment */}
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-sky-400" />
                    <span className="text-sm font-medium">Sentiment</span>
                  </div>
                  <div className="flex gap-4">
                    <span className="text-xs">Agent: <span className="capitalize font-medium">{analysis.sentiment?.agent}</span></span>
                    <span className="text-xs">Patient: <span className="capitalize font-medium">{analysis.sentiment?.patient}</span></span>
                  </div>
                </div>
              </div>

              {/* Issues */}
              {((vagueStatements.length > 0) || (analysis.unprofessional_statements?.length > 0)) && (
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Flagged Statements</h3>
                  <div className="space-y-2" data-testid="call-detail-flagged-moments">
                    {vagueStatements.map((s, i) => (
                      <div key={`v-${i}`} className="flex items-start gap-2 px-3 py-2 rounded bg-amber-500/5 border border-amber-500/20">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-medium text-amber-300">Vague ({s.severity})</span>
                          <p className="text-xs text-muted-foreground mt-0.5">{s.text}</p>
                          <p className="text-xs text-amber-100/90 mt-1">Reason: {s.reason || 'Reason not provided by the model.'}</p>
                        </div>
                      </div>
                    ))}
                    {(analysis.unprofessional_statements || []).map((s, i) => (
                      <div key={`u-${i}`} className="flex items-start gap-2 px-3 py-2 rounded bg-rose-500/5 border border-rose-500/20">
                        <XCircle className="w-3.5 h-3.5 text-rose-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-medium text-rose-300">Unprofessional ({s.severity})</span>
                          <p className="text-xs text-muted-foreground mt-0.5">{s.text}</p>
                          <p className="text-xs text-rose-100/90 mt-1">Reason: {s.reason || 'Reason not provided by the model.'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Coaching & Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Key Findings</h3>
                  <ul className="space-y-1">
                    {(analysis.key_findings || []).map((f, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex gap-2"><span className="text-primary">-</span>{f}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Coaching Tips</h3>
                  <ul className="space-y-1">
                    {(analysis.coaching_tips || []).map((t, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex gap-2"><span className="text-amber-400">-</span>{t}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {analysis.summary && (
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Summary</h3>
                  <p className="text-sm text-muted-foreground">{analysis.summary}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'voice' && (
        <div className="space-y-4" data-testid="call-detail-voice-metrics">
          {!voiceMetrics.pitch ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">No voice metrics available</div>
          ) : (
            <>
              {voiceMetricsSource && (
                <div className="text-[11px] text-muted-foreground font-mono">
                  Source: {voiceMetricsSource}
                </div>
              )}
              {/* Voice KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded-xl p-4">
                  <Volume2 className="w-4 h-4 text-teal-400 mb-2" />
                  <p className="text-lg font-semibold tabular-nums">{voiceMetrics.pitch?.mean_hz?.toFixed(0)} Hz</p>
                  <p className="text-xs text-muted-foreground">Avg Pitch</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <Volume2 className="w-4 h-4 text-sky-400 mb-2" />
                  <p className="text-lg font-semibold tabular-nums">{voiceMetrics.speech_rate?.estimated_wpm?.toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">Est. WPM</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <Volume2 className="w-4 h-4 text-amber-400 mb-2" />
                  <p className="text-lg font-semibold tabular-nums">{((voiceMetrics.silence?.silence_ratio || 0) * 100).toFixed(1)}%</p>
                  <p className="text-xs text-muted-foreground">Silence Ratio</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <Volume2 className="w-4 h-4 text-rose-400 mb-2" />
                  <p className="text-lg font-semibold tabular-nums">{voiceMetrics.silence?.num_pauses || 0}</p>
                  <p className="text-xs text-muted-foreground">Pauses</p>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Pitch Over Time</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={pitchData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" />
                      <XAxis hide />
                      <YAxis tick={{ fontSize: 10, fill: '#A6B2C7' }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Line type="monotone" dataKey="value" stroke="#22D3C5" dot={false} strokeWidth={1.5} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Energy Over Time</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={energyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(220,34%,20%)" />
                      <XAxis hide />
                      <YAxis tick={{ fontSize: 10, fill: '#A6B2C7' }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="value" fill="#2BB6FF" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Detailed metrics */}
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Detailed Metrics</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                  <div><span className="text-muted-foreground block">Pitch Range</span>{voiceMetrics.pitch?.min_hz?.toFixed(0)} - {voiceMetrics.pitch?.max_hz?.toFixed(0)} Hz</div>
                  <div><span className="text-muted-foreground block">Pitch Variability</span>{voiceMetrics.pitch?.variability?.toFixed(3)}</div>
                  <div><span className="text-muted-foreground block">Mean RMS Energy</span>{voiceMetrics.energy?.mean_rms?.toFixed(4)}</div>
                  <div><span className="text-muted-foreground block">Dynamic Range</span>{voiceMetrics.energy?.dynamic_range?.toFixed(4)}</div>
                  <div><span className="text-muted-foreground block">Speech Duration</span>{voiceMetrics.silence?.speech_duration_s?.toFixed(1)}s</div>
                  <div><span className="text-muted-foreground block">Total Duration</span>{voiceMetrics.silence?.total_duration_s?.toFixed(1)}s</div>
                  <div><span className="text-muted-foreground block">Spectral Centroid</span>{voiceMetrics.spectral?.mean_centroid_hz?.toFixed(0)} Hz</div>
                  <div><span className="text-muted-foreground block">Zero Crossing Rate</span>{voiceMetrics.spectral?.mean_zcr?.toFixed(4)}</div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Gemini Translation — output of the daily Gemini cron pipeline */}
      {activeTab === 'gemini-translation' && (
        <div className="space-y-2" data-testid="call-detail-gemini-translation">
          {call.gemini_status === 'failed' && (
            <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-3 text-xs text-rose-200">
              Gemini pipeline failed: {describeProcessingError(call.gemini_error)}
            </div>
          )}
          {call.gemini_translate_model && (
            <div className="text-[11px] text-muted-foreground font-mono">
              Translated by {call.gemini_translate_model}
              {call.gemini_processed_at && ` · ${new Date(call.gemini_processed_at).toLocaleString()}`}
            </div>
          )}
          {!hasGeminiTranscript ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
              {call.gemini_status === 'processing' ? 'Gemini pipeline is running…' : 'No Gemini translation yet. Will run at 19:00 IST.'}
            </div>
          ) : geminiSegments.map((seg, i) => {
            let role = geminiSpeakerRoles['Speaker ' + seg.speaker] || geminiSpeakerRoles[seg.speaker] || (seg.speaker === '1' ? 'agent' : 'patient');
            const searchTermArray = ['patient', 'Patient/Caller', 'agent', 'system'];
            const fullString = Object.values(geminiSpeakerRoles).find(val => typeof val === 'string' &&
              searchTermArray.some(term => val.toLowerCase().includes(term.toLowerCase()))
            );
            role = role ? role : fullString ? fullString.toLowerCase() : '';
            const isAgentRole = (role === 'agent') || (role.includes('Agent'));
            const isPatientRole = (role === 'patient') || (role === 'caller') || (role.includes('Patient/Caller'));
            const label = isAgentRole ? 'Agent' : isPatientRole ? 'Patient/Caller' : `Speaker ${seg.speaker}`;
            const labelColor = isAgentRole ? 'text-teal-300' : isPatientRole ? 'text-sky-300' : 'text-muted-foreground';
            const segClass = isAgentRole ? 'speaker-agent' : isPatientRole ? 'speaker-caller' : 'bg-card border border-border';
            const originalText = String(seg.original_text ?? seg.text ?? '').trim();
            const englishText = String(seg.english_text ?? seg.text ?? '').trim();
            const showOriginal = originalText && englishText && originalText !== englishText;
            return (
              <div key={i} className={`rounded-lg p-3 ${segClass}`}>
                <div className="flex items-center gap-2 mb-1">
                  {isAgentRole ? <Headphones className="w-3.5 h-3.5 text-teal-400" /> : <User className="w-3.5 h-3.5 text-sky-400" />}
                  <span className={`text-xs font-medium ${labelColor}`}>{label}</span>
                  <span className="text-[10px] text-muted-foreground font-mono">Speaker {seg.speaker}</span>
                  {seg.start_ms !== undefined && (
                    <span className="text-[10px] text-muted-foreground font-mono">{(seg.start_ms / 1000).toFixed(1)}s</span>
                  )}
                  {seg.source_lang && seg.source_lang !== 'en' && seg.source_lang !== 'unknown' && (
                    <span className="text-[10px] text-muted-foreground font-mono uppercase">{seg.source_lang}</span>
                  )}
                </div>
                {showOriginal && (
                  <p className="text-xs text-muted-foreground mb-1 italic">Original: {originalText}</p>
                )}
                <p className="text-sm">{englishText}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Gemini Analysis — same shape as Claude analysis, different source model */}
      {activeTab === 'gemini-analysis' && (
        <div className="space-y-4" data-testid="call-detail-gemini-analysis">
          {call.gemini_status === 'failed' && (
            <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-3 text-xs text-rose-200">
              Gemini pipeline failed: {describeProcessingError(call.gemini_error)}
            </div>
          )}
          {call.gemini_analysis_model && (
            <div className="text-[11px] text-muted-foreground font-mono">
              Analyzed by {call.gemini_analysis_model}
              {call.gemini_processed_at && ` · ${new Date(call.gemini_processed_at).toLocaleString()}`}
            </div>
          )}
          {!hasGeminiAnalysis || !geminiAnalysis.overall_score ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
              {call.gemini_status === 'processing' ? 'Gemini pipeline is running…' : 'No Gemini analysis yet. Will run at 19:00 IST.'}
            </div>
          ) : (
            <>
              <div className="bg-card border border-border rounded-xl p-4">
                {!isAgent && (
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`text-4xl font-bold tabular-nums ${geminiScoreColor}`}>{geminiOverallScore}</div>
                    <div>
                      <p className="text-sm font-medium">Overall Quality Score (Gemini)</p>
                      <p className="text-xs text-muted-foreground">{geminiAnalysis.call_purpose?.replace(/_/g, ' ')} - {geminiAnalysis.call_outcome?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <ScoreGauge score={geminiAnalysis.grammar_score || 0} label="Grammar" reason={geminiAnalysis.grammar_score_reason} />
                  <ScoreGauge score={geminiAnalysis.professionalism_score || 0} label="Professionalism" reason={geminiAnalysis.professionalism_score_reason} />
                  <ScoreGauge score={geminiAnalysis.empathy_score || 0} label="Empathy" reason={geminiAnalysis.empathy_score_reason} />
                  <ScoreGauge score={geminiAnalysis.communication_clarity || 0} label="Clarity" reason={geminiAnalysis.communication_clarity_reason} />
                  <ScoreGauge score={geminiAnalysis.greeting_quality || 0} label="Greeting" reason={geminiAnalysis.greeting_quality_reason} />
                  <ScoreGauge score={geminiAnalysis.closing_quality || 0} label="Closing" reason={geminiAnalysis.closing_quality_reason} />
                  <ScoreGauge score={geminiAnalysis.solution_provided?.score || 0} label="Solution" reason={geminiAnalysis.solution_provided?.reason} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {geminiAnalysis.solution_provided?.provided ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
                    <span className="text-sm font-medium">Solution {geminiAnalysis.solution_provided?.provided ? 'Provided' : 'Not Provided'}</span>
                  </div>
                  {geminiAnalysis.solution_provided?.evidence && <p className="text-xs text-muted-foreground italic">{geminiAnalysis.solution_provided.evidence}</p>}
                </div>

                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {geminiAnalysis.escalation_detected?.detected ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                    <span className="text-sm font-medium">Escalation {geminiAnalysis.escalation_detected?.detected ? 'Detected' : 'Not Detected'}</span>
                  </div>
                  {geminiAnalysis.escalation_detected?.reason && <p className="text-xs text-muted-foreground">{geminiAnalysis.escalation_detected.reason}</p>}
                </div>

                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {geminiAnalysis.blame_shifting?.detected ? <AlertTriangle className="w-4 h-4 text-rose-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                    <span className="text-sm font-medium">Blame Shifting {geminiAnalysis.blame_shifting?.detected ? 'Detected' : 'Not Detected'}</span>
                  </div>
                  {geminiAnalysis.blame_shifting?.evidence && <p className="text-xs text-muted-foreground italic">"{geminiAnalysis.blame_shifting.evidence}"</p>}
                </div>

                <div className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-sky-400" />
                    <span className="text-sm font-medium">Sentiment</span>
                  </div>
                  {(geminiSentiment?.agent || geminiSentiment?.patient) ? (
                    <div className="flex gap-4">
                      <span className="text-xs">Agent: <span className="capitalize font-medium">{geminiSentiment.agent || '-'}</span></span>
                      <span className="text-xs">Patient: <span className="capitalize font-medium">{geminiSentiment.patient || '-'}</span></span>
                    </div>
                  ) : geminiSentiment?.overall ? (
                    <p className="text-xs font-medium">{geminiSentiment.overall}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">No sentiment data available.</p>
                  )}
                </div>
              </div>

              {((geminiVagueStatements.length > 0) || (geminiUnprofessionalStatements.length > 0)) && (
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Flagged Statements</h3>
                  <div className="space-y-2" data-testid="call-detail-gemini-flagged-moments">
                    {geminiVagueStatements.map((s, i) => (
                      <div key={`gv-${i}`} className="flex items-start gap-2 px-3 py-2 rounded bg-amber-500/5 border border-amber-500/20">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-medium text-amber-300">Vague{s.severity ? ` (${s.severity})` : ''}</span>
                          <p className="text-xs text-muted-foreground mt-0.5">{s.text}</p>
                          <p className="text-xs text-amber-100/90 mt-1">Reason: {s.reason || 'Reason not provided by the model.'}</p>
                        </div>
                      </div>
                    ))}
                    {geminiUnprofessionalStatements.map((s, i) => (
                      <div key={`gu-${i}`} className="flex items-start gap-2 px-3 py-2 rounded bg-rose-500/5 border border-rose-500/20">
                        <XCircle className="w-3.5 h-3.5 text-rose-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-xs font-medium text-rose-300">Unprofessional{s.severity ? ` (${s.severity})` : ''}</span>
                          <p className="text-xs text-muted-foreground mt-0.5">{s.text}</p>
                          <p className="text-xs text-rose-100/90 mt-1">Reason: {s.reason || 'Reason not provided by the model.'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Key Findings</h3>
                  <ul className="space-y-1">
                    {(geminiAnalysis.key_findings || []).map((f, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex gap-2"><span className="text-primary">-</span>{f}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Coaching Tips</h3>
                  <ul className="space-y-1">
                    {(geminiAnalysis.coaching_tips || []).map((t, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex gap-2"><span className="text-amber-400">-</span>{t}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {geminiAnalysis.summary && (
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Summary</h3>
                  <p className="text-sm text-muted-foreground">{geminiAnalysis.summary}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
